import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateOneToOneTutorRateStudentType,
  adminDeleteOneToOneTutorRateStudentType,
  adminGetOneToOneTutorRateStudentTypes,
  adminGetOneToOneTutorRateStudentTypesForDropdown,
  adminUpdateOneToOneTutorRateStudentType,
  adminVerifyOneToOneTutorRateStudentTypeDelete,
} from './adminApi';

export interface IOneToOneTutorRateStudentTypeGrade {
  curriculum: string;
  grade: string;
}

export interface IOneToOneTutorRateStudentType {
  _id: string;
  name: string;
  grades: IOneToOneTutorRateStudentTypeGrade[];
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IOneToOneTutorRateStudentTypeDeleteVerificationData {
  oneToOneTutorRateCount: number;
}

export interface IInitialOneToOneTutorRateStudentTypesState {
  status: 'idle' | 'loading' | 'rejected';
  adminOneToOneTutorRateStudentTypeListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IOneToOneTutorRateStudentType>;
  };
  adminOneToOneTutorRateStudentTypeListDataForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IOneToOneTutorRateStudentType>;
  };
  adminOneToOneTutorRateStudentTypeDeleteVerificationData?: IOneToOneTutorRateStudentTypeDeleteVerificationData;
}

const initialState: IInitialOneToOneTutorRateStudentTypesState = {
  status: 'idle',
  adminOneToOneTutorRateStudentTypeListData: undefined,
  adminOneToOneTutorRateStudentTypeListDataForDropDown: undefined,
  adminOneToOneTutorRateStudentTypeDeleteVerificationData: undefined,
};

export const OneToOneTutorRateStudentTypesSlice = createSlice({
  initialState,
  name: 'oneToOneTutorRateStudentTypes',
  reducers: {
    resetAdminOneToOneTutorRateStudentTypeListData: (state) => {
      state.adminOneToOneTutorRateStudentTypeListData = undefined;
    },
    resetAdminOneToOneTutorRateStudentTypeListDataForDropDown: (state) => {
      state.adminOneToOneTutorRateStudentTypeListDataForDropDown = undefined;
    },
    resetAdminOneToOneTutorRateStudentTypeDeleteVerificationData: (state) => {
      state.adminOneToOneTutorRateStudentTypeDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(
        adminGetOneToOneTutorRateStudentTypes.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminOneToOneTutorRateStudentTypeListData = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminOneToOneTutorRateStudentTypeListData = newAdminOneToOneTutorRateStudentTypeListData;
        }
      )
      .addCase(
        adminGetOneToOneTutorRateStudentTypesForDropdown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminOneToOneTutorRateStudentTypeListDataForDropDown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminOneToOneTutorRateStudentTypeListDataForDropDown = newAdminOneToOneTutorRateStudentTypeListDataForDropDown;
        }
      )
      .addCase(
        adminCreateOneToOneTutorRateStudentType.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminOneToOneTutorRateStudentTypeListData) {
            const newAdminOneToOneTutorRateStudentTypeListData = {
              ...state.adminOneToOneTutorRateStudentTypeListData,
            };

            newAdminOneToOneTutorRateStudentTypeListData.list.unshift(result);

            if (newAdminOneToOneTutorRateStudentTypeListData.totalDocs) {
              newAdminOneToOneTutorRateStudentTypeListData.totalDocs += 1;
            } else {
              newAdminOneToOneTutorRateStudentTypeListData.totalDocs = 1;
            }

            state.adminOneToOneTutorRateStudentTypeListData = newAdminOneToOneTutorRateStudentTypeListData;
          }
        }
      )
      .addCase(
        adminUpdateOneToOneTutorRateStudentType.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminOneToOneTutorRateStudentTypeListData &&
            state.adminOneToOneTutorRateStudentTypeListData.list &&
            state.adminOneToOneTutorRateStudentTypeListData.list.length
          ) {
            const newAdminOneToOneTutorRateStudentTypeListData = {
              ...state.adminOneToOneTutorRateStudentTypeListData,
            };

            const index = newAdminOneToOneTutorRateStudentTypeListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminOneToOneTutorRateStudentTypeListData.list[index] = result;
            }

            state.adminOneToOneTutorRateStudentTypeListData = newAdminOneToOneTutorRateStudentTypeListData;
          }
        }
      )
      .addCase(
        adminDeleteOneToOneTutorRateStudentType.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminOneToOneTutorRateStudentTypeListData &&
            state.adminOneToOneTutorRateStudentTypeListData.list &&
            state.adminOneToOneTutorRateStudentTypeListData.list.length
          ) {
            const newAdminOneToOneTutorRateStudentTypeListData = {
              ...state.adminOneToOneTutorRateStudentTypeListData,
            };

            newAdminOneToOneTutorRateStudentTypeListData.totalDocs = newAdminOneToOneTutorRateStudentTypeListData.totalDocs
              ? newAdminOneToOneTutorRateStudentTypeListData.totalDocs - 1
              : 0;

            newAdminOneToOneTutorRateStudentTypeListData.list = newAdminOneToOneTutorRateStudentTypeListData.list.filter(
              (item) => item._id !== result._id
            );

            state.adminOneToOneTutorRateStudentTypeListData = newAdminOneToOneTutorRateStudentTypeListData;
          }
        }
      )
      .addCase(
        adminVerifyOneToOneTutorRateStudentTypeDelete.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminOneToOneTutorRateStudentTypeDeleteVerificationData = result;
        }
      );

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        adminCreateOneToOneTutorRateStudentType,
        adminGetOneToOneTutorRateStudentTypes,
        adminUpdateOneToOneTutorRateStudentType,
        adminDeleteOneToOneTutorRateStudentType,
        adminGetOneToOneTutorRateStudentTypesForDropdown,
        adminVerifyOneToOneTutorRateStudentTypeDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        adminCreateOneToOneTutorRateStudentType,
        adminGetOneToOneTutorRateStudentTypes,
        adminUpdateOneToOneTutorRateStudentType,
        adminDeleteOneToOneTutorRateStudentType,
        adminGetOneToOneTutorRateStudentTypesForDropdown,
        adminVerifyOneToOneTutorRateStudentTypeDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateOneToOneTutorRateStudentType,
        adminGetOneToOneTutorRateStudentTypes,
        adminUpdateOneToOneTutorRateStudentType,
        adminDeleteOneToOneTutorRateStudentType,
        adminGetOneToOneTutorRateStudentTypesForDropdown,
        adminVerifyOneToOneTutorRateStudentTypeDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getOneToOneTutorRateStudentTypesState = (
  state: RootState
): RootState['tutorRateStudentTypes'] => state.tutorRateStudentTypes;

export const {
  resetAdminOneToOneTutorRateStudentTypeListData,
  resetAdminOneToOneTutorRateStudentTypeListDataForDropDown,
  resetAdminOneToOneTutorRateStudentTypeDeleteVerificationData,
} = OneToOneTutorRateStudentTypesSlice.actions;

export * from './adminApi';
export default OneToOneTutorRateStudentTypesSlice.reducer;
